import type {
  AttendanceEventSummary,
  AttendanceType,
  CancelAttendanceEventHttpResult,
  CancelAttendanceEventParams,
  CancelAttendanceEventResponseBody,
  CreateAttendanceEventParams,
  CreateAttendanceEventResponse,
  JourneyMarkState,
} from '@src/models/interfaces/attendanceEvent.interface';
import { ATTENDANCE_ACTOR_ROLE } from '@src/models/interfaces/attendanceEvent.interface';
import { AttendanceEventMapper } from '@src/models/mappers/AttendanceEventMapper';
import AttendanceEventRepository from '@src/repositories/AttendanceEventRepository';
import { DateUtil } from '@src/packages/utils/dates';
import safeExecute from '@src/util/safeExecute';

export interface RegisterEmployeeMarkingInput {
  organizationId: string;
  attendanceRecordId: string;
  employeeId: string;
  attendanceType: AttendanceType;
  actorFullname?: string;
  timeZone?: string;
  createdAtDevice?: number;
}

class AttendanceEventService {
  private readonly repository: AttendanceEventRepository;

  constructor(repository: AttendanceEventRepository = new AttendanceEventRepository()) {
    this.repository = repository;
  }

  /**
   * Creates an attendance event (marking). Validates the required payload (mirrors the
   * server's contract) before hitting the Cloud Function so we fail fast on bad input.
   */
  async createAttendanceEvent(
    params: CreateAttendanceEventParams,
  ): Promise<CreateAttendanceEventResponse> {
    return safeExecute(
      async () => {
        const {
          organizationId,
          attendanceRecordId,
          employeeId,
          attendanceType,
          actorId,
          actorRole,
          timeZone,
        } = params ?? {};

        if (
          !organizationId ||
          !attendanceRecordId ||
          !employeeId ||
          !attendanceType ||
          !actorId ||
          !actorRole ||
          !timeZone
        ) {
          throw new Error(
            'Missing required parameters: organizationId, attendanceRecordId, employeeId, attendanceType, actorId, actorRole, timeZone',
          );
        }

        return this.repository.createAttendanceEvent(params);
      },
      'AttendanceEventService.createAttendanceEvent',
      {
        organizationId: params?.organizationId,
        attendanceRecordId: params?.attendanceRecordId,
        attendanceType: params?.attendanceType,
      },
    );
  }

  /**
   * Convenience builder for the marking portal / kiosk: an employee registering their own
   * marking. Fills the EMPLOYEE actor role, the display timezone and the device clock, so the
   * caller only supplies the session context and the marking type.
   */
  async registerEmployeeMarking(
    input: RegisterEmployeeMarkingInput,
  ): Promise<CreateAttendanceEventResponse> {
    const {
      organizationId,
      attendanceRecordId,
      employeeId,
      attendanceType,
      actorFullname,
      timeZone,
      createdAtDevice,
    } = input ?? {};

    const params: CreateAttendanceEventParams = {
      organizationId,
      attendanceRecordId,
      employeeId,
      attendanceType,
      actorId: employeeId,
      actorRole: ATTENDANCE_ACTOR_ROLE.EMPLOYEE,
      timeZone: timeZone || DateUtil.getDefaultTimeZone(),
      createdAtDevice: createdAtDevice ?? Date.now(),
      ...(actorFullname ? { actorFullname } : {}),
    };

    return this.createAttendanceEvent(params);
  }

  async cancelAttendanceEvent(
    data: CancelAttendanceEventParams,
  ): Promise<CancelAttendanceEventResponseBody> {
    return this.repository.cancelAttendanceEvent(data);
  }

  async cancelAttendanceEventByToken(token: string): Promise<CancelAttendanceEventHttpResult> {
    return this.repository.cancelAttendanceEventByToken(token);
  }

  /**
   * Lists stored attendance events for a record (mapped summaries).
   */
  async listByAttendanceRecord(
    organizationId: string,
    attendanceRecordId: string,
  ): Promise<AttendanceEventSummary[]> {
    return safeExecute(
      async () => {
        if (!organizationId?.trim() || !attendanceRecordId?.trim()) {
          return [];
        }

        const rawDocs = await this.repository.listByAttendanceRecord({
          organizationId,
          attendanceRecordId,
        });

        return (rawDocs ?? [])
          .map(doc => AttendanceEventMapper.fromFirestoreDoc(doc))
          .filter((event): event is AttendanceEventSummary => event != null);
      },
      'AttendanceEventService.listByAttendanceRecord',
      { organizationId, attendanceRecordId },
    );
  }

  /**
   * Resolves mark/break CTA state from `attendance_events` for the given record.
   * Prefer this over denormalized `actualBreak*` fields on the attendance record.
   */
  async getJourneyMarkState(
    organizationId: string,
    attendanceRecordId: string,
  ): Promise<JourneyMarkState> {
    const events = await this.listByAttendanceRecord(organizationId, attendanceRecordId);
    return AttendanceEventMapper.resolveJourneyMarkState(events);
  }
}

export default AttendanceEventService;
